/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import moment from 'moment';
import Shimmer from 'react-shimmer-effect';
import IcomoonReact from 'icomoon-react';
import ReadOnlyExerciseItem from './ReadOnlyExerciseItem';
import DraggableMonthExerciseItem from './DraggableMonthExerciseItem';
import { wasCalendarDayDropdownJustClosed } from './CalendarDropdown';
import {
  setViewType,
  setSelectedDate,
  setSelectedMonth,
  setSelectedWeekViewDayToggle,
  setWeekViewExpandedDay,
} from '../../ducks/calendarActions';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const ContentContainer = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 4px;
  padding-top: ${(props) => !props.hasBefore && '14px'};
  flex: 1;
  /* No overflow here — MonthContainer is the single scrollable for the
   * whole month grid. Per-cell overflow-y would make rbd treat this
   * element as the closest scrollable ancestor for the Droppable and
   * miss MonthContainer scroll, leaving droppable positions stale once
   * the user scrolls during a drag. Tall days now grow their row; the
   * user scrolls MonthContainer to see everything. */
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
`;

const RecurringHeader = styled('div')`
  flex-direction: column;
  display: flex;
  border-radius: 4px;
  padding-top: 8px;
  padding-bottom: 8px;
  background-color: #F8F8F8;
  margin-top: ${(props) => !props.isBefore && '5px'};
`;

const IconContainerBefore = styled('div')`
  cursor: pointer;
  display: flex;
  border-radius: 4px;
`;

const IconContainer = styled('div')`
  cursor: pointer;
  display: flex;
  border-radius: 4px;
`;

const IconContainerWrapper = styled('div')`
  position: relative;
  background: #444444;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #444444;
  min-height: 25px;
  min-width: 25px;
  max-width: 25px;
  max-height: 25px;
  margin-left: 2px;
`;

const LineContainer = styled('div')`
  position: relative;
  flex-grow: 1;
  margin-left: 5px;
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 2px;
    background-color: #444444;
    display: flex;
    align-items: center;
  }
  width: 100%;
  overflow: hidden;
`;

const RecurringText = styled('div')`
  margin-left: 7px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 13px;
`;

const MonthDayContent = ({
  date,
  exercises,
  isLoading,
  droppableProvided,
  isInteractive,
}) => {
  const dispatch = useDispatch();
  const formatDate = (d) => `${moment(d).format('YYYY-MM-DD')}`;
  const selectedWeekViewDayToggle = useSelector(
    (state) => state.calendar.data.selectedWeekViewDayToggle,
  );
  const beforeRecurringWorkouts = useSelector(
    (state) => state.calendar.data.beforeRecurringWorkouts,
  );
  const afterRecurringWorkouts = useSelector(
    (state) => state.calendar.data.afterRecurringWorkouts,
  );
  const athleteAddedItems = useSelector(
    (state) => state.calendar.data.calendarItems[formatDate(date)]?.athleteAddedItems,
  );
  const dragIsInProgress = useSelector(
    (state) => state.calendar.data.dragIsInProgress,
  );
  const draggedItem = useSelector((state) => state.calendar.data.draggedItem);

  /** Mirrors week view's `shouldCollapseUnderSessionBreak`
   * (DraggableDivWrapper.js:150). When a session break in this day is
   * being dragged, all items between it and the next session break (or
   * the end of the day) collapse visually so the session "follows" its
   * break. The Draggable wrappers stay mounted to preserve rbd indices —
   * only their inner content and footprint go to zero. */
  const itemsHiddenByDraggedBreak = useMemo(() => {
    if (!draggedItem || draggedItem.type !== 'session_break') return null;
    const breakIndex = exercises.findIndex(
      (ex) => String(ex.assignedId) === String(draggedItem.assignedId),
    );
    if (breakIndex === -1) return null; /** dragged break belongs to another day */
    const hidden = new Set();
    for (let i = breakIndex + 1; i < exercises.length; i += 1) {
      if (exercises[i]?.type === 'session_break') break;
      hidden.add(String(exercises[i].assignedId));
    }
    return hidden;
  }, [draggedItem, exercises]);

  /** rbd suppresses post-drag clicks at the document capture phase, but
   * same-Droppable reorders occasionally let a synthetic React onClick
   * through anyway. Track when a drag just ended and ignore clicks
   * inside a short window so reordering doesn't trip the cell's
   * switch-to-week-view handler. */
  const dragWasInProgress = useRef(false);
  const dragEndedAt = useRef(0);
  useEffect(() => {
    if (dragWasInProgress.current && !dragIsInProgress) {
      dragEndedAt.current = Date.now();
    }
    dragWasInProgress.current = dragIsInProgress;
  }, [dragIsInProgress]);

  const handleDayClick = () => {
    if (dragIsInProgress || Date.now() - dragEndedAt.current < 250) return;
    /** rc-trigger calls CalendarDropdown's onVisibleChange synchronously
     * from its outside-click handler, which records a timestamp. If a
     * day-header menu was just dismissed (within 200ms), this click is
     * the dismissal — bail before navigating to week view. */
    if (wasCalendarDayDropdownJustClosed()) return;
    // Reset week view toggle if needed (matches CalendarCalendarPicker pattern)
    if (selectedWeekViewDayToggle !== 'Everyday') {
      dispatch(setSelectedWeekViewDayToggle('Everyday'));
    }

    // Find the Sunday of the week for the clicked date to maintain week arrangement
    const sundayOfWeek = moment(date).startOf('week');
    const dateToSet = sundayOfWeek.toDate();
    dispatch(setSelectedDate(dateToSet));

    // Update selected month to match the clicked date
    const selectedMonthNum = moment(date).month() + 1;
    dispatch(setSelectedMonth(selectedMonthNum));

    // Switch to week view
    const dateFormatted = date.format('YYYY-MM-DD');
    dispatch(setViewType('weekView'));
    dispatch(setWeekViewExpandedDay(dateFormatted));
  };

  const hasBefore = beforeRecurringWorkouts[formatDate(date)]
      && beforeRecurringWorkouts[formatDate(date)].length > 0;

  /** Droppable wiring: when MonthDayContent is rendered inside a
   * react-beautiful-dnd `Droppable`, the parent passes its `provided`
   * bag through `droppableProvided`. We attach innerRef + droppableProps
   * to ContentContainer and render `provided.placeholder` after the
   * draggable items so rbd can size the destination correctly during a
   * drag. */
  const containerRef = droppableProvided?.innerRef;
  const containerDroppableProps = droppableProvided?.droppableProps || {};

  if (isLoading) {
    return (
      <ContentContainer
        ref={containerRef}
        onClick={handleDayClick}
        {...containerDroppableProps}
      >
        {[1, 2, 3].map((_, i) => (
          <Shimmer key={i}>
            <div style={{ height: 40, backgroundColor: '#f5f5f5', marginBottom: 4 }} />
          </Shimmer>
        ))}
        {droppableProvided?.placeholder}
      </ContentContainer>
    );
  }

  // Find the index of the first session_break
  const firstSessionBreakIndex = exercises.findIndex((ex) => ex.type === 'session_break');

  return (
    <ContentContainer
      ref={containerRef}
      onClick={handleDayClick}
      hasBefore={hasBefore}
      {...containerDroppableProps}
    >
      {beforeRecurringWorkouts[formatDate(date)]
      && beforeRecurringWorkouts[formatDate(date)].length > 0 ? (
        <RecurringHeader isBefore>
          <IconContainerBefore>
            <IconContainerWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='recurring-circle'
                color='white'
              />
            </IconContainerWrapper>
            <LineContainer>
              <RecurringText>
                Recurring (
                {beforeRecurringWorkouts[formatDate(date)].length}
                )
              </RecurringText>
            </LineContainer>
          </IconContainerBefore>
        </RecurringHeader>
        ) : null}
      {exercises.map((exercise, index) => {
        const itemKey = `${date.format('YYYY-MM-DD')}-${exercise.assignedId || exercise.id || index}`;
        const hasSessionBreakBackground = firstSessionBreakIndex !== -1
          && index >= firstSessionBreakIndex;
        const isHiddenByDraggedBreak = !!itemsHiddenByDraggedBreak
          && itemsHiddenByDraggedBreak.has(String(exercise.assignedId));

        if (isInteractive && exercise.assignedId !== undefined) {
          return (
            <DraggableMonthExerciseItem
              key={itemKey}
              content={exercise}
              index={index}
              exercises={exercises}
              hasSessionBreakBackground={hasSessionBreakBackground}
              isHiddenByDraggedBreak={isHiddenByDraggedBreak}
            />
          );
        }

        return (
          <ReadOnlyExerciseItem
            key={itemKey}
            content={exercise}
            index={index}
            exercises={exercises}
            date={date}
            hasSessionBreakBackground={hasSessionBreakBackground}
          />
        );
      })}
      {droppableProvided?.placeholder}
      {afterRecurringWorkouts[formatDate(date)]
      && afterRecurringWorkouts[formatDate(date)].length > 0 ? (
        <RecurringHeader>
          <IconContainer>
            <IconContainerWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='recurring-circle'
                color='white'
              />
            </IconContainerWrapper>
            <LineContainer>
              <RecurringText>
                Recurring (
                {afterRecurringWorkouts[formatDate(date)].length}
                )
              </RecurringText>
            </LineContainer>
          </IconContainer>
        </RecurringHeader>
        ) : null}
      {athleteAddedItems
      && athleteAddedItems.length > 0 ? (
        <RecurringHeader>
          <IconContainer>
            <IconContainerWrapper>
              <IcomoonReact
                iconSet={iconSet}
                size={15}
                icon='user-checkmark'
                color='white'
              />
            </IconContainerWrapper>
            <LineContainer>
              <RecurringText>
                Athlete Added Items (
                {athleteAddedItems.length}
                )
              </RecurringText>
            </LineContainer>
          </IconContainer>
        </RecurringHeader>
        ) : null}
    </ContentContainer>
  );
};

export default MonthDayContent;
